import {
  FormControl,
  FormLabel,
  FormErrorMessage,
} from '@chakra-ui/form-control'
import { Input } from '@chakra-ui/input'
import { Text as ChakraText, VStack } from '@chakra-ui/layout'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Switch,
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik, Form } from 'formik'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Req } from 'use-http'
import * as Yup from 'yup'
import type { UserSessionResponse } from '../../../interfaces'
import type SettingsResponse from '../../../interfaces/SettingsResponse'
import type {
  NamedTransformErrorResponse,
  NamedTransformCreatedSuccessResponse,
  NamedTransformEditedSuccessResponse,
  NamedTransformUpdate,
} from '../../../interfaces/SettingsResponse'
import useToast from '../../../hooks/useToast'
import { encodeHash } from '../../../../utils'

interface Props {
  currentNamedTransform: string | null
  values: SettingsResponse
  user: UserSessionResponse | null
  isOpen: boolean
  request: Req
  getData(): Promise<void>
  onClose(): void
}

const NamedTransformsEditModal = ({
  isOpen,
  onClose,
  currentNamedTransform,
  user,
  values,
  request,
  getData,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const [errorDesc, setErrorDesc] = useState<string>('')

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setErrorDesc('')
        onClose()
      }}
      size="xl"
    >
      <ModalOverlay />
      <Formik<NamedTransformUpdate>
        validationSchema={Yup.object().shape({
          label: Yup.string()
            .required('Required')
            .matches(
              /^[0-9a-zA-Z_-]*$/gu,
              "Name may contain only alphanumeric characters or '_', '-'",
            ),
          value: Yup.string()
            .required('Required')
            .matches(/^tr:/gu, "Transformations must begin with 'tr:'"),
        })}
        initialValues={
          currentNamedTransform
            ? values.namedTransform[currentNamedTransform]
            : { label: '', value: '', disabled: false }
        }
        onSubmit={async (formValues, _actions) => {
          setErrorDesc('')
          if (user?.clientNumber) {
            let response:
              | NamedTransformEditedSuccessResponse
              | NamedTransformCreatedSuccessResponse
              | NamedTransformErrorResponse
            if (currentNamedTransform) {
              response = (await request.put(
                `api/clients/${user.clientNumber}/named-transformations/${encodeHash(currentNamedTransform)}`,
                {
                  id: currentNamedTransform,
                  label: formValues.label,
                  value: formValues.value,
                  disabled: formValues.disabled ? 'yes' : 'no',
                },
              )) as
                | NamedTransformEditedSuccessResponse
                | NamedTransformErrorResponse
            } else {
              response = (await request.post(
                `api/clients/${user.clientNumber}/named-transformations`,
                {
                  label: formValues.label,
                  value: formValues.value,
                  disabled: formValues.disabled ? 'yes' : 'no',
                },
              )) as
                | NamedTransformCreatedSuccessResponse
                | NamedTransformErrorResponse
            }
            if (request.error) {
              if ((response as NamedTransformErrorResponse).reason) {
                setErrorDesc((response as NamedTransformErrorResponse).reason)
              } else {
                setErrorDesc(t('something_went_wrong', 'Something went wrong!'))
              }
            } else {
              await getData()
              if (currentNamedTransform) {
                toast({
                  description: t(
                    'successfully_edited_transform',
                    'Successfully edited named transform',
                  ),
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
              } else {
                toast({
                  description: t(
                    'successfully_created_transform',
                    'Successfully created named transform',
                  ),
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
              }
              setErrorDesc('')
              onClose()
            }
          }
        }}
      >
        {({ setFieldValue, errors, touched }) => (
          <Form>
            <ModalContent>
              <ModalHeader>
                {currentNamedTransform
                  ? t('edit_named_transform', 'Edit Named Transform')
                  : t('new_named_transform', 'New Named Transform')}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing="2.5em">
                  {errorDesc ? (
                    <Alert status="error">
                      <AlertIcon />
                      <AlertDescription>{errorDesc}</AlertDescription>
                    </Alert>
                  ) : null}

                  <Field name="label">
                    {({ field }: FieldProps<string, NamedTransformUpdate>) => (
                      <FormControl
                        marginTop="1em"
                        isRequired
                        isInvalid={Boolean(errors.label && touched.label)}
                      >
                        <FormLabel>{t('name', 'Name')}</FormLabel>
                        <ChakraText marginBottom="0.5em">
                          {t('named_transform_name_description')}
                        </ChakraText>
                        <Input {...field} placeholder="Eg: small_thumb" />

                        <FormErrorMessage marginBottom="-2.0408em">
                          {errors.label}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="value">
                    {({ field }: FieldProps<string, NamedTransformUpdate>) => (
                      <FormControl
                        marginTop="1em"
                        isRequired
                        isInvalid={Boolean(errors.value && touched.value)}
                      >
                        <FormLabel>
                          {t('transformation_string', 'Transformation String')}
                        </FormLabel>
                        <ChakraText marginBottom="0.5em">
                          {t(
                            'named_transform_transformation_string_description',
                          )}
                        </ChakraText>
                        <Input {...field} placeholder="tr:w-40,h-30" />
                        <FormErrorMessage marginBottom="-2.0408em">
                          {errors.value}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="disabled">
                    {({
                      field,
                    }: FieldProps<
                      boolean | undefined,
                      NamedTransformUpdate
                    >) => (
                      <FormControl
                        marginTop="1em"
                        isRequired
                        display="flex"
                        alignItems="center"
                        style={{ gap: '1em' }}
                      >
                        <Switch
                          name="disabled"
                          id="disabled"
                          isChecked={!field.value}
                          onChange={e => {
                            setFieldValue('disabled', !e.target.checked)
                          }}
                        />
                        <FormLabel margin={0}>{t('enabled')}</FormLabel>
                      </FormControl>
                    )}
                  </Field>
                </VStack>
              </ModalBody>
              <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <Button onClick={onClose} variant="outline">
                  {t('cancel', 'Cancel')}
                </Button>
                <Button type="submit">{t('submit', 'Submit')}</Button>
              </ModalFooter>
            </ModalContent>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

export default NamedTransformsEditModal
